"use client";

import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";
import { RegisterForm, RoleStep, type Role } from "@/components/auth/RegisterForm";
import { SocialAuthButtons } from "@/components/auth/SocialAuthButtons";
import { setTokens } from "@/lib/api";
import { getSafePostLoginRedirect } from "@/lib/utils/auth-helpers";
import { useAuthStore } from "@/store/authStore";
import type { User } from "@/lib/types";

export default function RegisterPage() {
  const setAuth = useAuthStore((state) => state.setAuth);
  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  useEffect(() => {
    const role = new URLSearchParams(window.location.search).get("role");
    if (role === "seafarer" || role === "shore" || role === "employer" || role === "institute") {
      setSelectedRole(role);
      return;
    }
    setSelectedRole(null);
  }, []);

  const handleSocialAuthSuccess = (data: unknown) => {
    const result = data as {
      accessToken?: string;
      refreshToken?: string;
      user?: User | null;
    };

    if (result.accessToken && result.refreshToken && result.user) {
      setTokens(result.accessToken, result.refreshToken);
      setAuth(result.user, result.accessToken);

      const redirect = new URLSearchParams(window.location.search).get("redirect");
      window.location.href = getSafePostLoginRedirect(result.user, redirect);
    }
  };

  return (
    <AuthShell
      variant="register"
      eyebrow="Built for maritime professionals"
      title="Create your CrewAhead account"
      description="Join a maritime careers platform designed for seafarers, shore professionals and employers."
    >
      {apiError && (
        <div
          role="alert"
          aria-live="assertive"
          className="mb-6 flex items-start gap-3 rounded-lg border border-danger/20 bg-danger/5 p-3 text-sm text-danger"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{apiError}</span>
        </div>
      )}

      <RoleStep selectedRole={selectedRole} onSelect={setSelectedRole} />

      <div className="pt-6">
        <SocialAuthButtons
          mode="register"
          selectedRole={selectedRole}
          onSuccess={handleSocialAuthSuccess}
          onError={(msg) => setApiError(msg)}
        />
      </div>

      {selectedRole && (
        <div className="pt-2">
          <RegisterForm selectedRole={selectedRole} onRoleChange={setSelectedRole} />
        </div>
      )}
    </AuthShell>
  );
}
